import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { type AvatarId } from "@jumpbean/shared";
import {
  Avatar,
  Button,
  Card,
  CardSkeleton,
  useToast,
} from "../components";
import { AddChildModal } from "../components/AddChildModal";
import { EditChildModal, type ChildData } from "../components/EditChildModal";
import { UpgradePrompt } from "../components/UpgradePrompt";
import { ParentLock } from "../components/ParentLock";
import { useAppStore } from "../stores";
import { usePageTitle } from "../hooks";
import { apiGet } from "../lib/api";

interface ChildWithStats {
  id: string;
  displayName: string;
  avatarId: string | null;
  dateOfBirth: string | null;
  createdAt: string;
  updatedAt: string;
  stats?: {
    totalStars: number;
    totalBadges: number;
  };
}

interface ChildrenResponse {
  children: ChildWithStats[];
  limit: number;
  canAddMore: boolean;
}

/**
 * ProfilesPage - Child profile selection page.
 *
 * Features:
 * - Display all child profiles for the parent account
 * - Large, tappable profile cards with recent progress
 * - Add new child profile with avatar selection
 * - Edit/delete profiles (parent-only controls)
 * - Subscription limit handling with upgrade prompts
 * - Satisfying selection animations
 */
export function ProfilesPage() {
  usePageTitle("Select Profile");

  const navigate = useNavigate();
  const { addToast } = useToast();
  const setSelectedChildId = useAppStore((state) => state.setSelectedChildId);

  // Data state
  const [children, setChildren] = useState<ChildWithStats[]>([]);
  const [childLimit, setChildLimit] = useState(1);
  const [canAddMore, setCanAddMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [editingChild, setEditingChild] = useState<ChildData | null>(null);

  // Selection animation state
  const [selectingChildId, setSelectingChildId] = useState<string | null>(null);

  // Fetch children on mount
  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await apiGet<ChildrenResponse>("/api/children");

      if (apiError || !data) {
        throw new Error(apiError?.message || "Failed to load profiles");
      }

      setChildren(data.children);
      setChildLimit(data.limit);
      setCanAddMore(data.canAddMore);
    } catch (err) {
      setError("Unable to load profiles. Please try again.");
      addToast({
        type: "encourage",
        message: "Couldn't load profiles. Tap to retry!",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChild = useCallback(
    (childId: string) => {
      // Trigger selection animation
      setSelectingChildId(childId);

      // Delay navigation for satisfying animation
      setTimeout(() => {
        setSelectedChildId(childId);
        navigate("/play");
      }, 400);
    },
    [setSelectedChildId, navigate]
  );

  const handleAddClick = useCallback(() => {
    if (canAddMore) {
      setShowAddModal(true);
    } else {
      setShowUpgradePrompt(true);
    }
  }, [canAddMore]);

  const handleAddSuccess = useCallback(
    (newChild: { id: string; displayName: string; avatarId: string; dateOfBirth: string | null }) => {
      setChildren((prev) => [
        ...prev,
        {
          ...newChild,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ]);
      setCanAddMore(children.length + 1 < childLimit);
      setShowAddModal(false);
      addToast({
        type: "success",
        message: `${newChild.displayName} is ready to play!`,
      });
    },
    [children.length, childLimit, addToast]
  );

  const handleEditClick = useCallback(
    (child: ChildWithStats, e: React.MouseEvent) => {
      e.stopPropagation();
      setEditingChild({
        id: child.id,
        displayName: child.displayName,
        avatarId: child.avatarId || "bunny",
        dateOfBirth: child.dateOfBirth ?? null,
      });
      setShowEditModal(true);
    },
    []
  );

  const handleEditUpdate = useCallback(
    (updated: ChildData) => {
      setChildren((prev) =>
        prev.map((c) =>
          c.id === updated.id
            ? {
                ...c,
                displayName: updated.displayName,
                avatarId: updated.avatarId,
                updatedAt: new Date().toISOString(),
              }
            : c
        )
      );
      setShowEditModal(false);
      setEditingChild(null);
      addToast({
        type: "success",
        message: "Profile updated!",
      });
    },
    [addToast]
  );

  const handleEditDelete = useCallback(
    (childId: string) => {
      const deletedChild = children.find((c) => c.id === childId);
      setChildren((prev) => prev.filter((c) => c.id !== childId));
      setCanAddMore(true);
      setShowEditModal(false);
      setEditingChild(null);
      addToast({
        type: "info",
        message: `${deletedChild?.displayName || "Profile"} removed`,
      });
    },
    [children, addToast]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center px-4 py-8">
        <h1 className="font-display font-bold text-display-md text-magic-600 text-center mb-8">
          Who's Playing Today?
        </h1>
        <div className="grid grid-cols-2 gap-6 max-w-md w-full">
          <CardSkeleton className="h-[180px]" />
          <CardSkeleton className="h-[180px]" />
        </div>
      </div>
    );
  }

  // Error state
  if (error && children.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
        <div className="w-24 h-24 rounded-full bg-fairy-100 flex items-center justify-center mb-6">
          <span className="text-5xl" role="img" aria-label="Sad face">
            😢
          </span>
        </div>
        <h2 className="font-display font-bold text-display-sm text-neutral-800 mb-3">
          Oops! Something went wrong
        </h2>
        <p className="font-body text-body-lg text-neutral-600 mb-8 max-w-xs">
          {error}
        </p>
        <Button variant="primary" size="lg" onClick={fetchChildren}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center px-4 py-8 min-h-[80vh]">
      {/* Header with decorative elements */}
      <div className="relative mb-8">
        {/* Floating decorations */}
        <div className="absolute -top-4 -left-8 text-base animate-float opacity-60">
          ⭐
        </div>
        <div
          className="absolute -top-2 -right-6 text-base animate-float opacity-60"
          style={{ animationDelay: "0.5s" }}
        >
          ✨
        </div>

        <h1 className="font-display font-bold text-display-md text-magic-600 text-center">
          Who's Playing Today?
        </h1>
      </div>

      {/* Profiles Grid */}
      <div className="grid grid-cols-2 gap-6 max-w-md w-full mb-8">
        {children.map((child, index) => (
          <div
            key={child.id}
            className="animate-[fadeInUp_400ms_ease-out_both]"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <ProfileCard
              child={child}
              isSelecting={selectingChildId === child.id}
              onSelect={() => handleSelectChild(child.id)}
              onEdit={(e) => handleEditClick(child, e)}
            />
          </div>
        ))}

        {/* Add Child Card */}
        <div
          className="animate-[fadeInUp_400ms_ease-out_both]"
          style={{ animationDelay: `${children.length * 100}ms` }}
        >
          <button
            onClick={handleAddClick}
            className="w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-fairy-400 rounded-2xl group"
          >
            <Card
              variant="interactive"
              className="flex flex-col items-center justify-center p-6 min-h-[180px] border-2 border-dashed border-neutral-300 hover:border-magic-400 transition-all duration-300 group-hover:bg-magic-50/30"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-magic-100 to-magic-200 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 mx-auto">
                <PlusIcon className="w-8 h-8 text-magic-500" />
              </div>
              <span className="font-display font-bold text-body-lg text-neutral-500 group-hover:text-magic-600 transition-colors text-center block">
                Add Child
              </span>
              {!canAddMore && (
                <span className="font-body text-sm text-neutral-400 mt-1 text-center block">
                  Upgrade for more
                </span>
              )}
            </Card>
          </button>
        </div>
      </div>

      {/* Empty state message */}
      {children.length === 0 && (
        <div className="text-center mt-4 animate-[fadeIn_500ms_ease-out]">
          <p className="font-body text-body-lg text-neutral-500">
            Add your first child to start the adventure! 🚀
          </p>
        </div>
      )}

      {/* Profile limit indicator */}
      {children.length > 0 && (
        <p className="font-body text-body-sm text-neutral-400 mt-4">
          {children.length} of {childLimit} profile{childLimit === 1 ? "" : "s"}
        </p>
      )}

      {/* Modals */}
      <AddChildModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />

      <EditChildModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingChild(null);
        }}
        child={editingChild}
        onUpdate={handleEditUpdate}
        onDelete={handleEditDelete}
      />

      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        currentLimit={childLimit}
      />
    </div>
  );
}

// ==================== Sub-components ====================

interface ProfileCardProps {
  child: ChildWithStats;
  isSelecting: boolean;
  onSelect: () => void;
  onEdit: (e: React.MouseEvent) => void;
}

function ProfileCard({ child, isSelecting, onSelect, onEdit }: ProfileCardProps) {
  const avatarId = (child.avatarId || "bunny") as AvatarId;
  const hasStats = child.stats && (child.stats.totalStars > 0 || child.stats.totalBadges > 0);

  return (
    <div className="relative group">
      {/* Edit button - requires long-press to unlock (parent-only control) */}
      <div className="absolute -top-2 -right-2 z-10">
        <ParentLock holdDuration={600}>
          <button
            onClick={onEdit}
            className="
              w-10 h-10
              bg-cloud
              rounded-full
              flex items-center justify-center
              border-2 border-neutral-200
              shadow-md
              transition-all duration-200
              hover:border-magic-400 hover:scale-110
              focus:outline-none focus-visible:ring-2 focus-visible:ring-fairy-400
            "
            aria-label={`Edit ${child.displayName}'s profile`}
          >
            <EditIcon className="w-4 h-4 text-neutral-500" />
          </button>
        </ParentLock>
      </div>

      {/* Main profile card */}
      <button
        onClick={onSelect}
        disabled={isSelecting}
        className={`
          w-full
          focus:outline-none focus-visible:ring-4 focus-visible:ring-fairy-400 rounded-2xl
          transition-all duration-300
          ${isSelecting ? "scale-110 animate-pulse" : ""}
        `}
      >
        <Card
          variant="interactive"
          className={`
            flex flex-col items-center p-6
            transition-all duration-300
            ${isSelecting ? "border-sunshine-400 shadow-[0_0_30px_rgba(255,217,61,0.4)]" : ""}
          `}
        >
          {/* Avatar with selection glow */}
          <div className={`relative mb-4 transition-transform duration-300 ${isSelecting ? "scale-125" : ""}`}>
            <Avatar avatarId={avatarId} size="xl" name={child.displayName} />

            {/* Selection sparkles */}
            {isSelecting && (
              <>
                <div className="absolute -top-2 -left-2 text-lg animate-ping">✨</div>
                <div className="absolute -top-1 -right-3 text-lg animate-ping" style={{ animationDelay: "0.1s" }}>⭐</div>
                <div className="absolute -bottom-2 -right-1 text-lg animate-ping" style={{ animationDelay: "0.2s" }}>✨</div>
              </>
            )}
          </div>

          {/* Name */}
          <span className="font-display font-bold text-body-xl text-neutral-800 mb-1">
            {child.displayName}
          </span>

          {/* Stats indicator */}
          {hasStats && (
            <div className="flex items-center justify-center gap-2 text-neutral-500">
              {child.stats!.totalStars > 0 && (
                <span className="flex items-center gap-1 font-body text-sm">
                  <span className="text-sunshine-500">⭐</span>
                  {child.stats!.totalStars}
                </span>
              )}
              {child.stats!.totalBadges > 0 && (
                <span className="flex items-center gap-1 font-body text-sm">
                  <span className="text-magic-500">🏅</span>
                  {child.stats!.totalBadges}
                </span>
              )}
            </div>
          )}
        </Card>
      </button>
    </div>
  );
}

// ==================== Icons ====================

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

// Add fadeInUp animation if not present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-profiles-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }
}

export default ProfilesPage;
